import { AppAbility, UpdateChannel } from "@core-services/portal"
import { InformationCircleIcon } from "@heroicons/react/20/solid"
import { zodResolver } from "@hookform/resolvers/zod"
import { Link, useSearch } from "@tanstack/react-router"
import {
  FormControl,
  FormControlLabel,
  Switch,
  TextField,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "@vindral/components"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { FormSection } from "../../../atoms/FormSection"
import { SaveButton } from "../../../atoms/SaveButton"
import { SaveSection } from "../../../atoms/SaveSection"
import { Message, MessageSection } from "../../../molecules/Message"
import { RouteBlockDialog } from "../../../molecules/RouteBlockDialog"
import { channelEditDetailsDrmRoute } from "../../../routes/channel/channel-edit"
import { RouterOutputs } from "../../../trpc"
import { ChannelFindById } from "../ChannelEdit"

type DrmConfig = RouterOutputs["drm"]["findByOrganizationId"]

const resolver = zodResolver(UpdateChannel)

export interface ChannelEditDetailsDrmProps {
  channel: ChannelFindById
  drm: DrmConfig | undefined
  ability: AppAbility
  onSave: (channel: UpdateChannel) => Promise<void>
}

export function ChannelEditDetailsDrm({ onSave, channel, drm }: ChannelEditDetailsDrmProps) {
  const searchParams = useSearch({ from: channelEditDetailsDrmRoute.id })

  const organizationSelected = !!searchParams.organizationId

  const drmProviderLink = (title: string) => (
    <Link
      className="font-medium text-fg underline decoration-solid-primary decoration-2 underline-offset-2 hover:decoration-fg"
      to={organizationSelected ? "/settings/drm" : `/organizations/edit/${channel.organization.publicId}/drm`}
    >
      {title}
    </Link>
  )

  const { register, handleSubmit, setValue, formState, reset, watch } = useForm({
    resolver,
    mode: "onChange",
    defaultValues: {
      channelId: channel.channelId,
      drm: {
        enabled: channel.drm?.enabled ?? false,
        contentId: channel.drm?.contentId ?? "",
      },
    },
  })

  const { errors, isDirty, isValid, isSubmitting, isSubmitSuccessful } = formState

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({}, { keepValues: true })
    }
  }, [isSubmitSuccessful, reset])

  const submit = async (updateChannel: UpdateChannel) => {
    const moqEnabled = updateChannel.drm?.enabled ? true : channel.moqEnabled
    await onSave({
      ...updateChannel,
      moqEnabled,
    })
  }

  const disabledForm = !drm
  const drmEnabled = watch("drm.enabled")

  return (
    <div className="space-y-4 py-4">
      <RouteBlockDialog condition={isDirty && !isSubmitting} />
      {!drm && (
        <Message severity="warning">
          <MessageSection>
            <div className="flex w-full">
              <div className="grow">
                <span>In order to enable DRM you will need to configure a </span> {drmProviderLink("DRM provider")}
              </div>
            </div>
          </MessageSection>
        </Message>
      )}
      {!channel.moqEnabled && drmEnabled && (
        <Message severity="warning">
          <MessageSection>
            <div className="flex w-full">
              <div className="grow">
                <span>Please note that enabling DRM will also enable MoQ for this channel</span>
              </div>
            </div>
          </MessageSection>
        </Message>
      )}

      <form onSubmit={handleSubmit(submit)} className="flex flex-col gap-8">
        <FormSection
          title="Digital Rights Management"
          description={
            <div>
              <p className="mb-4">
                Digital Rights Management (DRM) is a set of technologies and policies designed to protect copyrighted
                digital content from unauthorized access, distribution, and piracy.
              </p>

              <p>Visit your {drmProviderLink("organization settings")} to setup your DRM provider.</p>
            </div>
          }
          docsLink="https://docs.vindral.com/manage/drm"
        >
          <div className="flex flex-col gap-4">
            <FormControl>
              <FormControlLabel error={errors.drm?.enabled?.message}>Enable DRM protection</FormControlLabel>
              <Switch
                disabled={disabledForm}
                defaultChecked={channel.drm?.enabled ?? false}
                onCheckedChange={(value) => {
                  setValue("drm.enabled", value, { shouldDirty: true })
                }}
                label=""
              />
            </FormControl>

            <FormControl>
              <FormControlLabel error={errors.drm?.contentId?.message}>
                <div>
                  <div className="flex min-h-[28px] items-center justify-between gap-1 tabular-nums text-fg-subtle">
                    <div className="flex items-center text-sm font-medium uppercase tracking-wider">
                      <span className="mr-1">Content Id</span>

                      <TooltipProvider>
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger type="button">
                            <InformationCircleIcon className="h-4 w-4" />
                          </TooltipTrigger>
                          <TooltipPortal>
                            <TooltipContent side="right">
                              <p>For Irdeto this needs to be set to an existing contentId in the Irdeto system.</p>

                              <p className="mt-2">For EZDRM this can be left blank.</p>
                            </TooltipContent>
                          </TooltipPortal>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                </div>
              </FormControlLabel>

              <TextField
                {...register("drm.contentId")}
                type="text"
                error={!!errors.drm?.contentId?.message}
                disabled={disabledForm}
              />
            </FormControl>
          </div>
        </FormSection>

        <SaveSection>
          <SaveButton text="Save" disabled={!isDirty || !isValid} />
        </SaveSection>
      </form>
    </div>
  )
}
